/** @file AgentScreen: the revision log (who did what, filter human / agent) and "Copy prompt for the agent". */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { RevisionRow } from '@zilo/pose3d-api/types/restAdded';
import { useApi } from '../shell/useApi';
import { useEditor } from '../state/useEditor';
import { useEditorStore } from '../state/useEditorStore';
import { asPose3DError } from '../store/asPose3DError';
import { errorText } from '../store/errorText';
import { Button } from '../ui-kit/Button';
import { Tabs } from '../ui-kit/Tabs';
import { actorKind } from './actorKind';
import { agentPrompt } from './agentPrompt';

type Filter = 'all' | 'human' | 'agent';

const FILTERS: { id: Filter; label: string }[] = [{ id: 'all', label: 'All' }, { id: 'human', label: 'Human' }, { id: 'agent', label: 'Agent' }];

/** The last 100 revisions, newest first; refetched whenever the puppet's revision moves. */
function useRevisions(puppetId: string | null, revision: number): { rows: RevisionRow[]; error: string | null } {
  const api = useApi();
  const [rows, setRows] = useState<RevisionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (api === null || puppetId === null) return;
    let live = true;
    api.puppets.revisions(puppetId, { limit: 100 }).then((r) => {
      if (live) { setRows([...r.revisions].sort((a, b) => b.revision - a.revision)); setError(null); }
    }, (e: unknown) => { if (live) setError(errorText(asPose3DError(e))); });
    return () => { live = false; };
  }, [api, puppetId, revision]);
  return { rows, error };
}

/** Composes the prompt from the current state and copies it (falls back to showing it when the clipboard is blocked). */
function CopyPrompt(): ReactElement {
  const store = useEditorStore();
  const [shown, setShown] = useState<string | null>(null);
  const copy = (): void => {
    const s = store.getState();
    const text = agentPrompt({ puppet: s.puppet, selection: s.selection, playhead: s.playhead, url: s.session.url });
    navigator.clipboard.writeText(text).then(() => { s.notify('info', 'Prompt copied: paste it to your agent.'); }, () => { setShown(text); });
  };
  return (
    <div className="p3-stack">
      <div className="p3-row"><Button variant="primary" onClick={copy}>Copy prompt for the agent</Button></div>
      {shown === null ? null : <textarea className="p3-input p3-mono" readOnly rows={7} value={shown} aria-label="Prompt" />}
    </div>
  );
}

/** Revision log of the open puppet; without a server it says so. */
export function AgentScreen(): ReactElement {
  const api = useApi();
  const puppetId = useEditor((s) => s.puppet.id);
  const revision = useEditor((s) => s.puppet.revision);
  const [filter, setFilter] = useState<Filter>('all');
  const { rows, error } = useRevisions(puppetId, revision);
  const shown = rows.filter((r) => filter === 'all' || actorKind(r.actor) === filter);
  return (
    <div className="p3-screen">
      <div className="p3-screen-head"><h1>Agent</h1><Tabs label="Actor filter" tabs={FILTERS} active={filter} onSelect={setFilter} /></div>
      <CopyPrompt />
      {api === null ? <p className="p3-muted">No server: the revision log lives on the server.</p> : null}
      {error === null ? null : <p className="p3-error" role="alert">{error}</p>}
      <table className="p3-table" aria-label="Revisions">
        <thead><tr><th>Rev</th><th>Actor</th><th>When</th><th>Commands</th></tr></thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.revision}>
              <td className="p3-mono">{r.revision}</td>
              <td className="p3-mono">{actorKind(r.actor) === 'agent' ? `agent · ${r.actor}` : r.actor}</td>
              <td>{new Date(r.at).toLocaleString()}</td>
              <td className="p3-mono">{r.names.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
